"use client";

import { usePlayer } from "@/context/player-context";
import { createImageSizes, createImageUrl } from "@/utils/image";
import classNames from "classnames";
import Image from "next/image";
import Link from "next/link";
import { ReactEventHandler, useEffect, useRef, useState } from "react";
import { CustomCategory } from "./category";
import Icon from "./icon";

export const formatSeconds = (seconds: number) => {
  seconds = Math.floor(seconds);

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(remainingSeconds).padStart(2, "0");

  return `${formattedMinutes}:${formattedSeconds}`;
};

const PodcastPlayer = () => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showSeekInput, setShowSeekInput] = useState(false);
  const [seekMinutes, setSeekMinutes] = useState("");
  const [seekSeconds, setSeekSeconds] = useState("");

  const { podcast, isCollapsed, closePodcast, toggleCollapsed } = usePlayer();

  const onClose = () => {
    setShowSeekInput(false);
    setSeekMinutes("");
    setSeekSeconds("");
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);

    if (audioRef.current) {
      audioRef.current.src = "";
    }

    closePodcast();
  };

  useEffect(() => {
    setShowSeekInput(false);
    setSeekMinutes("");
    setSeekSeconds("");
    setIsPlaying(true);
    setCurrentTime(0);
    setDuration(0);

    if (audioRef.current && podcast?.fields.media?.mediaUrl) {
      audioRef.current.src = podcast?.fields.media?.mediaUrl;
      audioRef.current.play();
    }
  }, [podcast]);

  if (!podcast) {
    return null;
  }

  const onPlay = () => {
    audioRef.current?.play();
    setIsPlaying(true);
  };

  const onPause = () => {
    audioRef.current?.pause();
    setIsPlaying(false);
  };

  const onTimeUpdate: ReactEventHandler<HTMLAudioElement> = (event) => {
    setCurrentTime((event.target as HTMLAudioElement).currentTime);
  };

  const onRangeChange: ReactEventHandler<HTMLInputElement> = (event) => {
    const updatedCurrentTime = Number((event.target as HTMLInputElement).value);

    if (audioRef.current) {
      audioRef.current.currentTime = updatedCurrentTime;
    }

    setCurrentTime(updatedCurrentTime);
  };

  const onLoadedMetadata: ReactEventHandler<HTMLAudioElement> = (event) => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleMinutesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "");
    setSeekMinutes(value);
  };

  const handleSecondsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/\D/g, "");
    if (Number(value) > 59) value = "59";
    setSeekSeconds(value);
  };

  const podcastShow = podcast.fields["podcast-show"];

  return (
    <>
      <div className="fixed inset-x-0 bottom-0 z-10 bg-sand-700  border-t-[5px] border-sand-700 ">
        <div
          className={classNames(
            "container flex justify-between space-x-4 lg:space-x-8",
            isCollapsed ? "py-2" : "py-4 lg:p-8",
          )}
        >
          <div
            className={classNames("flex flex-1", isCollapsed && "items-center")}
          >
            {!isCollapsed && (
              <div className="relative w-[80px] h-[80px] flex-shrink-0 mr-4 lg:mr-8 hidden lg:block">
                {podcastShow?.fields.image?.fields.file?.url && (
                  <Image
                    fill
                    src={createImageUrl(
                      podcastShow.fields.image.fields.file.url,
                      { crop: "1:1" },
                    )}
                    alt={podcastShow.fields.image.fields.alt || ""}
                    className="rounded-sm object-cover"
                    sizes={createImageSizes({
                      mobile: "12vw",
                      tablet: "12vw",
                      desktop: "12vw",
                    })}
                  />
                )}
              </div>
            )}

            <div className="flex-1">
              {!isCollapsed && podcastShow && (
                <CustomCategory color="Podd" name={podcastShow.fields.title} />
              )}
              {podcast.fields.title && (
                <h2
                  className={classNames(
                    "font-semibold",
                    isCollapsed
                      ? "text-base line-clamp-1"
                      : "text-lg lg:text-2xl line-clamp-2",
                  )}
                >
                  {podcast.fields.title}
                </h2>
              )}

              <div
                className={classNames(
                  "flex items-center",
                  isCollapsed ? "mt-1" : "mt-2",
                )}
              >
                {isPlaying ? (
                  <button
                    onClick={onPause}
                    aria-label="Spela ljud"
                    aria-pressed={true}
                  >
                    <Icon.PauseInverted
                      bgClassName="fill-red-700"
                      className="fill-white-500 w-7 h-7"
                    />
                  </button>
                ) : (
                  <button
                    onClick={onPlay}
                    aria-label="Spela ljud"
                    aria-pressed={false}
                  >
                    <Icon.PlayInverted
                      bgClassName="fill-red-700"
                      className="fill-white-500 w-7 h-7"
                    />
                  </button>
                )}

                {!isCollapsed && (
                  <>
                    <button
                      className="flex-shrink-0 font-bold ml-1 bg-sand-800 rounded h-7 flex items-center justify-center px-2 text-sm"
                      onClick={() => setShowSeekInput(true)}
                    >
                      Gå till
                    </button>

                    <div className="text-sm text-gray-400  min-w-[50px] text-center">
                      {formatSeconds(currentTime)}
                    </div>

                    <input
                      value={currentTime}
                      type="range"
                      max={duration}
                      className="w-full max-w-[520px] h-[2px] bg-gray-300  appearance-none cursor-pointer"
                      onChange={onRangeChange}
                    />

                    <div className="text-sm text-gray-400  min-w-[50px] text-center">
                      {formatSeconds(duration)}
                    </div>
                  </>
                )}
              </div>

              {!isCollapsed && showSeekInput && (
                <form
                  className="space-x-1 mt-2"
                  onSubmit={(e) => {
                    e.preventDefault();

                    const minutes = Number(seekMinutes) || 0;
                    const seconds = Number(seekSeconds) || 0;
                    const timeInSeconds = minutes * 60 + seconds;

                    if (audioRef.current && timeInSeconds <= duration) {
                      audioRef.current.currentTime = timeInSeconds;
                      setCurrentTime(timeInSeconds);
                    }
                  }}
                >
                  <input
                    type="text"
                    placeholder="00"
                    inputMode="numeric"
                    value={seekMinutes}
                    onChange={handleMinutesChange}
                    className="bg-sand-200 border border-sand-800 placeholder:text-gray-400 placeholder:opacity-75 px-1 h-7 w-7 text-sm rounded"
                  />
                  <span className="text-white">:</span>
                  <input
                    type="text"
                    placeholder="00"
                    inputMode="numeric"
                    value={seekSeconds}
                    onChange={handleSecondsChange}
                    className="bg-sand-200 border border-sand-800 placeholder:text-gray-400 placeholder placeholder:opacity-75 px-1 h-7 w-7 text-sm rounded"
                  />
                  <button className="bg-red-700 hover:bg-red-800 text-static-white text-sm rounded font-bold px-2 h-7">
                    Gå till tidpunkt
                  </button>
                </form>
              )}
            </div>
          </div>

          <div className="flex flex-col-reverse justify-between items-end">
            {!isCollapsed &&
              (podcastShow?.fields.appleUrl ||
                podcastShow?.fields.spotifyUrl) && (
              <div className="space-y-1 mt-4">
                <div className="hidden lg:block uppercase text-sm">
                  Prenumerera
                </div>
                <div className="flex space-x-2 justify-end">
                  {podcastShow?.fields.appleUrl && (
                    <Link
                      href={podcastShow.fields.appleUrl}
                      aria-label="Apple podcasts"
                    >
                      <Icon.Podcast className="w-[15px] lg:w-[30px] h-auto" />
                    </Link>
                  )}

                  {podcastShow?.fields.spotifyUrl && (
                    <Link
                      href={podcastShow.fields.spotifyUrl}
                      aria-label="Spotify"
                    >
                      <Icon.Spotify className="w-[15px] lg:w-[30px] h-auto" />
                    </Link>
                  )}
                </div>
              </div>
            )}

            <div className="flex gap-2 mb-auto">
              <button
                aria-label={isCollapsed ? "Visa ljudspelaren" : "Dölj ljudspelaren"}
                aria-expanded={!isCollapsed}
                // Only needed when the player would cover most of a short
                // viewport (e.g. 400% zoom), matching #header in globals.css.
                // Kept visible while collapsed so it can always be reopened.
                className={classNames(
                  "btn-icon bg-sand-800 lg:h-10 lg:w-10",
                  !isCollapsed && "hidden [@media(max-height:500px)]:inline-flex",
                )}
                onClick={toggleCollapsed}
              >
                <Icon.ChevronDown
                  className={classNames("transition-transform", {
                    "rotate-180": isCollapsed,
                  })}
                />
              </button>

              <button
                aria-label="Stäng podd spelare"
                className="btn-icon bg-sand-800 lg:h-10 lg:w-10"
                onClick={onClose}
              >
                <Icon.Close className="fill-gray-300 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      <audio
        onTimeUpdate={onTimeUpdate}
        className="hidden"
        ref={audioRef}
        onLoadedMetadata={onLoadedMetadata}
      />
    </>
  );
};

export default PodcastPlayer;
